import { useState } from 'react';
import { Box, Typography, IconButton, Grid, Paper } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getAvailableWeekdays } from './bookingSlotUtils';

const BookingCalendar = ({ selectedDate, onDateSelect, hideContainer = false, disponibilidad }) => {
  const days = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const [currentMonthState, setCurrentMonthState] = useState(new Date());

  const currentMonthIndex = currentMonthState.getMonth();
  const currentYear = currentMonthState.getFullYear();
  const monthLabel = `${monthNames[currentMonthIndex]} ${currentYear}`;

  // null means all days available (no availability configured)
  const availableWeekdays = getAvailableWeekdays(disponibilidad);

  const handlePrevMonth = () => {
    setCurrentMonthState(new Date(currentYear, currentMonthIndex - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentMonthState(new Date(currentYear, currentMonthIndex + 1, 1));
  };

  const firstDayOfMonth = new Date(currentYear, currentMonthIndex, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  };

  const isDayDisabled = (date) => {
    if (date < today) return true;
    if (availableWeekdays === null) return false; // no config → all days open
    return !availableWeekdays.has(date.getDay());
  };

  const content = (
    <>
      <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 2 }}>
        Fecha
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" sx={{ bgcolor: '#F1F5F9' }} onClick={handlePrevMonth}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" fontWeight="bold" color="#0E2465">
          {monthLabel}
        </Typography>
        <IconButton size="small" sx={{ bgcolor: '#F1F5F9', border: '1px solid #7495f8', color: '#7495f8' }} onClick={handleNextMonth}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={1}>
        {days.map((day, i) => (
          <Grid item size={1.7} key={`${day}-${i}`} sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="#737791" fontWeight="bold">{day}</Typography>
          </Grid>
        ))}

        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <Grid item size={1.7} key={`empty-${i}`} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const date = new Date(currentYear, currentMonthIndex, dayNum);
          const isSelected = isSameDay(date, selectedDate);
          const disabled = isDayDisabled(date);
          const isToday = isSameDay(date, today);

          return (
            <Grid item size={1.7} key={dayNum} sx={{ textAlign: 'center' }}>
              <Box
                onClick={() => !disabled && onDateSelect(date)}
                sx={{
                  p: 1,
                  borderRadius: '8px',
                  cursor: disabled ? 'default' : 'pointer',
                  color: disabled ? '#E0E0E0' : (isSelected ? 'white' : '#0E2465'),
                  bgcolor: isSelected ? '#7495f8' : 'transparent',
                  border: isToday && !isSelected ? '1px solid #7495f8' : 'none',
                  '&:hover': {
                    bgcolor: disabled ? 'transparent' : (isSelected ? '#7495f8' : '#F1F5F9')
                  }
                }}
              >
                <Typography variant="caption" fontWeight="bold">{dayNum}</Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  if (hideContainer) return content;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', bgcolor: 'white', border: '1px solid #F1F5F9' }}>
      {content}
    </Paper>
  );
};

export default BookingCalendar;
